/* Importing the necessary libraries and files */
import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

/* Importing the components from the material-ui library. */
import { Breadcrumbs, Button, Card, CardContent, Container, Grid, Link, Stack, Typography } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';

/* Importing the functions from the Redux slices. */
import { getQuestionnaire } from '../../features/questionnaire/questionnaireSlice';
import { getQuestionnaireResponse } from '../../features/questionnaireResponse/questionnaireResponseSlice';

/* Importing components. */
import RenderQuestionnaireForm from './RenderQuestionnaireForm';
import AutoCompletePatient from '../../components/AutoCompletePatient';
import Page from '../../components/Page';

import fetchAndHandleData from '../../utils/fetchAndHandleData';
import mapQuestionnaireResponse from '../../utils/QuestionnaireResponse/mapQuestionnaireResponse';

/**
 * Renders the details page for a questionnaire response.
 * @returns {JSX.Element} The rendered component.
 */
export default function QuestionnaireResponseDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  /* Using the useSelector hook to get the Patients array from the Redux store. */
  const Questionnaires = useSelector((state) => state.questionnaire.Questionnaires);
  const QuestionnaireResponses = useSelector((state) => state.questionnaireResponse.QuestionnaireResponses);
  const pageQuestionnaire = Questionnaires.find((el) => el.id === params.id);
  const pageQuestionnaireResponse = QuestionnaireResponses.find((el) => el.id === params.responseID);
  
  const [values, setValues] = useState({});
  const [title, setTitle] = useState('Add Questionnaire Reponse');

  useEffect(() => {
    if (!pageQuestionnaire) return;
    setTitle(`Questionnaire Response Details- ${pageQuestionnaire.title}`);
  }, [pageQuestionnaire]);

  const setQuestionnaireForm = useCallback((data) => {
    Object.keys(data).forEach((key) => {
      setValues((prev) => ({ ...prev, [key]: data[key] }));
    });
  }, []);

  useEffect(() => {
    if (!pageQuestionnaire) return;
    if (!pageQuestionnaireResponse) return;

    const mappedAnswers = mapQuestionnaireResponse(pageQuestionnaire, pageQuestionnaireResponse);
    mappedAnswers.subject = pageQuestionnaireResponse.subject.reference.split('/')[1];
    setQuestionnaireForm(mappedAnswers);
  }, [pageQuestionnaire, pageQuestionnaireResponse, setQuestionnaireForm]);

  const initializeData = useCallback(async () => {
    await fetchAndHandleData({
      dispatch,
      navigate,
      fetchAction: getQuestionnaire,
      id: params.id,
      errorMessage: 'No Questionnaire record found',
      navigateOnFailure: true,
      navigateTo: '/questionnaire/list',
    });

    await fetchAndHandleData({
      dispatch,
      navigate,
      fetchAction: getQuestionnaireResponse,
      id: params.responseID,
      errorMessage: 'No Questionnaire Response record found',
      navigateOnFailure: true,
      navigateTo: `/questionnaireresponse/list/${params.id}`,
    });
  }, [dispatch, navigate, params.id, params.responseID]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  /* Rendering the header of the page. */
  const renderHeader = () => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
      <Stack direction="column" justifyContent="space-between">
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>

        <Breadcrumbs aria-label="breadcrumb" mr={4}>
          <Link underline="hover" color="inherit" component={RouterLink} to="/questionnaire/list">
            Questionnaire
          </Link>
          <Link
            underline="hover"
            color="inherit"
            component={RouterLink}
            to={`/questionnaireresponse/list/${params.id}`}
          >
            Questionnaire Response
          </Link>
          <Typography color="text.primary">{params.responseID}</Typography>
        </Breadcrumbs>
      </Stack>
      <Button
        variant="contained"
        component={RouterLink}
        to={`/questionnaireresponse/edit/${params.id}/${params.responseID}`}
        startIcon={<EditIcon />}
      >
        Edit Questionnaire Response
      </Button>
    </Stack>
  );

  return (
    <Page title={title}>
      <Container maxWidth="none">
        {renderHeader()}

        <Grid container>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Grid container item xs={12} columnSpacing={4} rowSpacing={3}>
                  <Grid item xs={12}>
                    <Grid item xs={12}>
                      <Typography variant="h4">Subject</Typography>
                    </Grid>
                    <AutoCompletePatient fieldName={'subject'} values={values} readOnly patientID={values?.subject} />
                  </Grid>
                  <RenderQuestionnaireForm item={pageQuestionnaire?.item} values={values} readOnly />
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
